import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NotebookPen } from 'lucide-react';
import EmptyState from 'components/ui/EmptyState';
import Skeleton from 'components/ui/skeleton';
import { SegmentedTabs } from 'components/ui/data-table';
import { StatTile } from 'components/ui/stat-tile';
import { useMistakes, type MistakeSkill } from 'modules/mistakes';
import MistakeCard from './MistakeCard';

type Filter = 'all' | MistakeSkill;
const SKILL_ORDER: MistakeSkill[] = ['listening', 'reading', 'sat_rw', 'sat_math'];

/** Wrong answers from the learner's IELTS and SAT attempts, to answer again and put right for points. */
export default function Mistakes() {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useMistakes();
  const [filter, setFilter] = useState<Filter>('all');
  const items = useMemo(() => data?.items ?? [], [data]);
  const skills = SKILL_ORDER.filter((skill) => items.some((m) => m.skill === skill));
  const shown = filter === 'all' ? items : items.filter((m) => m.skill === filter);

  if (isLoading) {
    return (
      <div className="space-y-3" aria-hidden>
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-40 w-full rounded-lg" />)}
      </div>
    );
  }
  if (isError) {
    return (
      <EmptyState
        icon={<NotebookPen />}
        title={t('mistakes.load_failed')}
        secondaryAction={{ label: t('retry'), onClick: () => void refetch() }}
      />
    );
  }
  if (items.length === 0) {
    return (
      <EmptyState
        icon={<NotebookPen />}
        title={t('mistakes.empty_title')}
        description={data?.mastered
          ? `${t('mistakes.all_learned', { count: data.mastered })}${data.points ? ` ${t('mistakes.points_total', { points: data.points })}` : ''}`
          : t('mistakes.empty_body')}
      />
    );
  }

  return (
    <section aria-labelledby="mistakes-heading">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 id="mistakes-heading" className="text-[15px] font-semibold text-foreground">
            {t('mistakes.title', { count: items.length })}
          </h2>
          <p className="mt-0.5 text-[13px] text-muted-foreground">{t('mistakes.subtitle')}</p>
        </div>
        {skills.length > 1 && (
          <SegmentedTabs<Filter>
            aria-label={t('mistakes.filter')}
            className="h-9"
            options={[
              { value: 'all' as const, label: t('mistakes.skill_all') },
              ...skills.map((skill) => ({ value: skill, label: t(`mistakes.skill_${skill}`) })),
            ]}
            value={filter}
            onChange={setFilter}
          />
        )}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2 sm:max-w-md sm:gap-3">
        <StatTile label={t('mistakes.stat_fixed')} value={data?.fixed ?? 0} />
        <StatTile label={t('mistakes.stat_points')} value={data?.points ?? 0} sub={t('mistakes.points_rule')} />
      </div>
      <ul className="mt-4 space-y-3">
        {shown.map((mistake) => <MistakeCard key={mistake.key} mistake={mistake} />)}
      </ul>
    </section>
  );
}
